import { MutableRefObject, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useIntl } from "react-intl";
import { useApiCall } from "@framework/react-hooks";

import {
  IResponseReport,
  IRequestReport,
  IResponseVisualStationAlarmStatus,
  IRequestVisualTrainComments,
  IResponseVisualTrainComments,
  IResponseJournalTrainTooltip,
  IRequestJournalTrainTooltip,
  IRequestVisualStationAlarmStatus,
} from "./interfaces";

export const useDownloadReport = () => {
  const { formatMessage } = useIntl();

  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const { fn: getReportBlob, isLoading: isReportDownloading } = useApiCall(
    async (api, { idReport }: { idReport: number }): Promise<Blob | undefined> => {
      try {
        return (await api.fetchBlob({
          url: `/report/${idReport}/pdf`,
          method: "GET",
        })) as Blob;
      } catch (error) {
        console.error(error);
        enqueueSnackbar(formatMessage({ id: "pages.journals.reports.downloadError" }), { variant: "error" });
        return undefined;
      }
    },
    { success: false, error: false },
  );

  const { fn: getReports, isLoading: isReportsLoading } = useApiCall(
    async (api, { idTrain, startTimestamp, endTimestamp }: IRequestReport) => {
      const json = (await api.fetchJson({
        url: "/report",
        method: "GET",
        data: { idTrain },
      })) as IResponseReport[];

      if (!json?.length) {
        enqueueSnackbar(formatMessage({ id: "pages.journals.reports.noReports" }), { variant: "info" });
        return;
      }

      const start = new Date(startTimestamp).getTime();
      const end = new Date(endTimestamp).getTime();

      const inRange = json.filter(r => {
        if (!r.Date) return true;
        const t = new Date(r.Date).getTime();
        return !Number.isNaN(t) && t >= start && t <= end;
      });

      if (!inRange.length) {
        enqueueSnackbar(formatMessage({ id: "pages.journals.reports.noReports" }), { variant: "info" });
        return;
      }

      const mostRecentReport = inRange.sort((a, b) => {
        const ta = a.Date ? new Date(a.Date).getTime() : 0;
        const tb = b.Date ? new Date(b.Date).getTime() : 0;
        return tb - ta;
      })[0];

      if (!mostRecentReport) {
        enqueueSnackbar(formatMessage({ id: "pages.journals.reports.noReports" }), { variant: "info" });
        return;
      }

      const blob = await getReportBlob(undefined, { idReport: mostRecentReport.idReport });
      if (blob) {
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setOpen(true);
      }
    },
    { success: false },
  );

  const close = () => {
    setOpen(false);
    setPdfUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  return {
    open,
    pdfUrl,
    close,
    downloadReportByTrainId: (dto: IRequestReport) => async () => {
      if (dto.idTrain) {
        await getReports(undefined, dto);
      }
    },
    isReportLoading: isReportsLoading || isReportDownloading,
  };
};

export const useGetTooltip = ({ abortControllerRef }: { abortControllerRef: MutableRefObject<AbortController> }) => {
  const { fn, isLoading } = useApiCall(
    async (api, { trainId, endTimestamp, startTimestamp }: IRequestJournalTrainTooltip) => {
      return api.fetchJson({
        url: `/journal/train/${trainId}/tooltip`,
        method: "GET",
        data: { startTimestamp, endTimestamp, length: 50 },
        signal: abortControllerRef.current.signal,
      }) as Promise<IResponseJournalTrainTooltip[]>;
    },
    { success: false },
  );
  return { getTooltip: fn, isLoading };
};

export const useGetVisualTooltipTrain = ({
  abortControllerRef,
}: {
  abortControllerRef: MutableRefObject<AbortController>;
}) => {
  const { fn, isLoading } = useApiCall(
    async (api, { trainId, date, precision }: IRequestVisualStationAlarmStatus) => {
      return api.fetchJson({
        url: `/visual/station/${trainId}/alarm-status`,
        method: "GET",
        data: {
          date,
          precision,
        },
        signal: abortControllerRef.current.signal,
      }) as Promise<IResponseVisualStationAlarmStatus[]>;
    },
    { success: false },
  );

  return {
    getTooltip: fn,
    isLoading,
  };
};

export const useGetVisualComments = ({
  abortControllerRef,
}: {
  abortControllerRef: MutableRefObject<AbortController>;
}) => {
  const { fn, isLoading } = useApiCall(
    async (api, { paramId, date, precision }: IRequestVisualTrainComments) => {
      return api.fetchJson({
        url: `/visual/train/${paramId}/comments`,
        method: "GET",
        data: { date, precision },
        signal: abortControllerRef.current.signal,
      }) as Promise<IResponseVisualTrainComments>;
    },
    { success: false, error: false },
  );

  return { fn, isLoading };
};

export interface ICommentFileResponse {
  blob: Blob;
  fileName: string;
}

export const useGetCommentFiles = () => {
  const { fn } = useApiCall(
    async (
      api,
      { commentId, signal }: { commentId: number; signal?: AbortSignal },
    ): Promise<ICommentFileResponse | undefined> => {
      if (!commentId) {
        return undefined;
      }

      const result = await api.fetchBlobWithFileName({
        url: `/visual/train/${commentId}/files`,
        method: "GET",
        signal,
      });

      if (!result) {
        return undefined;
      }

      return {
        blob: result.blob,
        fileName: result.fileName,
      };
    },
    { success: false },
  );

  return {
    getCommentFiles: fn,
  };
};
